import { FormEvent, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/panel";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import {
  FREE_TRIAL_ATTENDEES_PER_STREAM,
  FREE_TRIAL_DAYS,
  FREE_TRIAL_STREAMS,
} from "@/lib/trial";
import { conferenceWizardPath } from "@/lib/wizard-navigation";

export function OnboardingPage() {
  const { refresh, signOut, user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api<{ accountId: string }>("/api/accounts", {
        method: "POST",
        body: JSON.stringify({ name: "" }),
      });
      await refresh();
      navigate(conferenceWizardPath(), { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not create workspace",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-rs-bg px-4">
      <Panel className="w-full max-w-lg p-8">
        <h1 className="text-2xl font-semibold text-rs-ink">
          Create Your Workspace
        </h1>
        <p className="mt-2 text-sm text-rs-ink-muted">
          We will create a private workspace
          {user?.displayName ? ` for ${user.displayName}` : ""}. New workspaces
          include {FREE_TRIAL_STREAMS} sync streams for {FREE_TRIAL_DAYS} days,
          up to {FREE_TRIAL_ATTENDEES_PER_STREAM} attendees per stream.
        </p>
        <form className="mt-6 space-y-4" onSubmit={onSubmit}>
          {error ? <p className="text-sm text-rs-danger">{error}</p> : null}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              className="text-sm text-rs-ink-muted underline-offset-2 hover:text-rs-ink hover:underline"
              disabled={busy}
              onClick={() => void signOut().then(() => navigate("/login"))}
            >
              Sign Out
            </button>
            <Button type="submit" disabled={busy}>
              {busy ? "Creating…" : "Continue"}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
}
